import { Button, cn, Skeleton } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Construction, Play } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { EXCHANGE_STATUS_META } from '@/components/exchange-status-meta';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { EXCHANGE_MAINTENANCE_QUERY_KEY, useExchangeMaintenance } from '@/hooks/queries/exchange/use-exchange-maintenance';
import { useExchangeStatus } from '@/hooks/queries/exchange/use-exchange-status';
import { type ExchangeMaintenanceState, setExchangeMaintenance } from '@/services/api/exchange-maintenance';
import { StartMaintenanceConfirmDialog } from './exchange-action-dialogs';
import { describeAllowlist, isEmptyAllowlist } from './maintenance-allowlist';

const WHILE_ON = [
  'Broker requests that reach the exchange (orders, balances, accounts) are refused with 503 EXCHANGE_MAINTENANCE, unless you let them back in below.',
  'Nothing is sent to the exchange, so brokers know no order was placed or cancelled.',
  'Every broker is notified by webhook when it starts and when it ends.',
  'It stays on until you end it. Ending it also clears who was let back in.',
];

const FLIP_COPY: Record<ExchangeMaintenanceState, { title: string; description: string; actionLabel: string; success: string }> = {
  on: {
    title: 'Start maintenance',
    description: 'Requests that reach the exchange will be refused until maintenance is ended. This action is logged and auditable.',
    actionLabel: 'Start maintenance',
    success: 'Maintenance started',
  },
  off: {
    title: 'End maintenance',
    description: 'Everyone reaches the exchange again. Only do this once the exchange is confirmed back up. This action is logged and auditable.',
    actionLabel: 'End maintenance',
    success: 'Maintenance ended',
  },
};

interface FactProps {
  label: string;
  children: React.ReactNode;
}

const Fact = ({ label, children }: FactProps) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium">{children}</dd>
  </div>
);
Fact.displayName = 'Fact';

/**
 * The page's lead: whether the exchange is taking traffic, and the one action that changes it.
 * The coloured rule and headline carry the state; the facts underneath say what the exchange
 * itself reports and who is let through.
 */
export const MaintenanceStatePanel = () => {
  const queryClient = useQueryClient();
  const maintenance = useExchangeMaintenance();
  const status = useExchangeStatus();
  const { data } = maintenance;
  // Starting maintenance stops every broker's exchange traffic, so it gets the same staged confirm as freeze/kill
  const [isConfirmingStart, setIsConfirmingStart] = useState(false);
  const [isExplaining, setIsExplaining] = useState(false);

  const flip = (state: ExchangeMaintenanceState) => {
    const copy = FLIP_COPY[state];
    void confirmAction({
      title: copy.title,
      description: copy.description,
      actionLabel: copy.actionLabel,
      destructive: state === 'on',
      // A throw keeps the dialog open showing the API error (403, or saved-but-not-propagated)
      onConfirm: async (comment) => {
        let next: Awaited<ReturnType<typeof setExchangeMaintenance>>;
        try {
          next = await setExchangeMaintenance(state, comment);
        } catch (error) {
          // The write may still have landed (saved but not propagated): refresh in the background,
          // without letting a refresh failure replace the API error the dialog shows
          void queryClient.invalidateQueries({ queryKey: EXCHANGE_MAINTENANCE_QUERY_KEY });
          throw error;
        }
        // The PUT answers with the new state and allowlist (emptied when maintenance ends)
        queryClient.setQueryData(EXCHANGE_MAINTENANCE_QUERY_KEY, next);
        toast.success(copy.success);
        if (next.brokersNotified === false) {
          toast.warning('Brokers were not notified by webhook. Let them know directly.');
        }
      },
    });
  };

  if (maintenance.isError) {
    return <QueryErrorBanner error={maintenance.error} onRetry={() => maintenance.refetch()} title="Failed to fetch exchange maintenance" />;
  }

  if (!data) {
    return <Skeleton className="h-40 w-full rounded-lg" />;
  }

  const isOn = data.state === 'on';
  const exchangeStatus = status.data ? EXCHANGE_STATUS_META[status.data] : null;
  const lettingIn = isEmptyAllowlist(data.allowlist) ? 'Nobody' : describeAllowlist(data.allowlist);

  return (
    <section
      aria-labelledby="maintenance-state"
      className={cn('rounded-lg border border-l-4 bg-card', isOn ? 'border-amber-500/40 border-l-amber-500 bg-amber-500/5' : 'border-border border-l-emerald-500')}
    >
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-2xl font-semibold tracking-tight" id="maintenance-state">
            {isOn ? <Construction aria-hidden className="size-6 text-amber-500" /> : <span aria-hidden className="size-2.5 rounded-full bg-emerald-500" />}
            {isOn ? 'In maintenance' : 'Maintenance off'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isOn ? `Requests that reach the exchange are refused${isEmptyAllowlist(data.allowlist) ? '.' : `, except ${lettingIn}.`}` : 'Requests reach the exchange as normal.'}
          </p>
        </div>
        {isOn ? (
          <Button className="gap-2 self-start bg-emerald-600 text-white hover:bg-emerald-700" disabled={!data.canEdit} onClick={() => flip('off')} type="button">
            <Play className="size-4" />
            End maintenance
          </Button>
        ) : (
          <Button className="gap-2 self-start bg-amber-500 text-white hover:bg-amber-600" disabled={!data.canEdit} onClick={() => setIsConfirmingStart(true)} type="button">
            <Construction className="size-4" />
            Start maintenance
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-4 border-t px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
        <dl className="flex flex-wrap gap-x-10 gap-y-3">
          <Fact label="Exchange reports">
            {exchangeStatus ? (
              <span className={cn('inline-flex items-center gap-1.5', exchangeStatus.textClass)}>
                <span aria-hidden className={cn('size-2 rounded-full', exchangeStatus.dotClass)} />
                {exchangeStatus.label}
              </span>
            ) : status.isError ? (
              <span className="text-muted-foreground">Unavailable</span>
            ) : (
              <Skeleton className="h-5 w-20" />
            )}
          </Fact>
          <Fact label={isOn ? 'Letting in' : 'Will let in'}>{lettingIn}</Fact>
          {!data.canEdit ? <Fact label="Your access">View only</Fact> : null}
        </dl>
        <button
          aria-controls="maintenance-explainer"
          aria-expanded={isExplaining}
          className="inline-flex items-center gap-1 self-start rounded text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:self-auto"
          onClick={() => setIsExplaining((previous) => !previous)}
          type="button"
        >
          <ChevronRight aria-hidden className={cn('size-4 transition-transform motion-reduce:transition-none', isExplaining && 'rotate-90')} />
          What happens during maintenance
        </button>
      </div>

      {isExplaining ? (
        <ul className="list-disc space-y-1 border-t px-5 py-4 pl-10 text-sm text-muted-foreground" id="maintenance-explainer">
          {WHILE_ON.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}

      {isConfirmingStart ? (
        <StartMaintenanceConfirmDialog
          onCancel={() => setIsConfirmingStart(false)}
          onContinue={() => {
            setIsConfirmingStart(false);
            flip('on');
          }}
        />
      ) : null}
    </section>
  );
};
MaintenanceStatePanel.displayName = 'MaintenanceStatePanel';
